// v-decimal.js — sem recursão, com casas decimais flexíveis
export default {
  beforeMount(el, binding) {
    el.style.textAlign = 'right'
    el.setAttribute('inputmode', 'decimal')

    // Flag para evitar loop quando emitimos 'input' programaticamente
    let programmatic = false

    const getOpts = () => {
      const v = binding?.value || {}
      let min = Number.isInteger(v.min) ? v.min : 2
      let max = Number.isInteger(v.max) ? v.max : min
      min = Math.max(0, min)
      max = Math.max(min, Math.min(10, max))
      return { min, max }
    }

    const emitInput = () => {
      programmatic = true
      el.dispatchEvent(new Event('input', { bubbles: true }))
    }

    const parseLooseToDot = (v) => {
      v = (v || '').trim()
      if (!v) return ''
      v = v.replace(/\s+/g, '')

      const lastComma = v.lastIndexOf(',')
      const lastDot   = v.lastIndexOf('.')
      let decimalSep = null
      if (lastComma >= 0 || lastDot >= 0) {
        decimalSep = (lastComma > lastDot) ? ',' : '.'
      }

      let out = ''
      let usedDecimal = false
      for (const ch of v) {
        if (ch >= '0' && ch <= '9') out += ch
        else if ((ch === ',' || ch === '.') && decimalSep && ch === decimalSep && !usedDecimal) {
          out += '.'
          usedDecimal = true
        }
      }
      return out
    }

    const clampFraction = (raw, max) => {
      if (!raw) return ''
      const i = raw.indexOf('.')
      if (i === -1) return raw
      const int = raw.slice(0, i)
      const frac = raw.slice(i + 1, i + 1 + max)
      return max > 0 ? `${int}.${frac}` : int
    }

    const toBr = (num, { min, max }) =>
      new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: min,
        maximumFractionDigits: max,
        useGrouping: true
      }).format(num)

    // ===== Handlers =====
    const onInput = () => {
      if (programmatic) {               // input gerado pela diretiva
        programmatic = false
        return
      }
      const { max } = getOpts()
      const before = el.value
      let raw = parseLooseToDot(before)
      raw = clampFraction(raw, max)

      if (raw !== before) {
        // Atualizamos o valor e emitimos UM novo input (programático)
        el.value = raw
        emitInput()
      }
      // Se não mudou, não emitimos nada: o input nativo já ocorreu
    }

    const onPaste = () => {
      // Deixa o conteúdo colar, sanitiza em seguida
      requestAnimationFrame(() => {
        const { max } = getOpts()
        let raw = parseLooseToDot(el.value)
        raw = clampFraction(raw, max)
        if (raw !== el.value) {
          el.value = raw
          emitInput()
        }
      })
    }

    const onFocus = () => {
      // Volta para o “cru” (ponto) para editar
      const { max } = getOpts()
      let raw = parseLooseToDot(el.value)
      raw = clampFraction(raw, max)
      if (raw !== el.value) {
        el.value = raw
        emitInput()
      }
    }

    const onBlur = () => {
      const opts = getOpts()
      let raw = parseLooseToDot(el.value)
      raw = clampFraction(raw, opts.max)
      if (!raw) {
        el.value = ''
        emitInput()
        return
      }
      if (raw.endsWith('.')) raw = raw.slice(0, -1)

      const n = Number(raw)
      const formatted = Number.isFinite(n) ? toBr(n, opts) : ''
      if (formatted !== el.value) {
        el.value = formatted
        // Sincroniza modelo com o valor formatado para evitar “desformatação” em re-render
        emitInput()
      }
      el.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Guarda para remover
    el._decHandlers = { onInput, onPaste, onFocus, onBlur }
    el.addEventListener('input', onInput)
    el.addEventListener('paste', onPaste)
    el.addEventListener('focus', onFocus)
    el.addEventListener('blur', onBlur)
  },

  mounted(el, binding) {
    // Formata valor inicial (se houver)
    const v = (el.value || '').trim()
    if (!v) return
    const min = Number.isInteger(binding?.value?.min) ? binding.value.min : 2
    const max = Number.isInteger(binding?.value?.max)
      ? binding.value.max
      : (Number.isInteger(binding?.value?.min) ? binding.value.min : 2)
    const n = Number(v.replace(/\./g, '').replace(',', '.'))
    if (Number.isFinite(n)) {
      el.value = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: Math.max(0, min),
        maximumFractionDigits: Math.max(0, max),
        useGrouping: true
      }).format(n)
    }
  },

  unmounted(el) {
    const h = el._decHandlers
    if (!h) return
    el.removeEventListener('input', h.onInput)
    el.removeEventListener('paste', h.onPaste)
    el.removeEventListener('focus', h.onFocus)
    el.removeEventListener('blur', h.onBlur)
    delete el._decHandlers
  }
}
